import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { getDialogueDetail } from '../api/client';

const getScoreColor = (score: number) => {
  if (score >= 80) return '#10b981';
  if (score >= 60) return '#f59e0b';
  return '#ef4444';
};

const getScoreBg = (score: number) => {
  if (score >= 80) return '#ecfdf5';
  if (score >= 60) return '#fef3c7';
  return '#fef2f2';
};

const DialogueDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const [data, setData] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [backHover, setBackHover] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    if (id) {
      getDialogueDetail(parseInt(id))
        .then((res) => setData(res.data))
        .catch(console.error)
        .finally(() => setLoading(false));
    }
  }, [id]);

  if (loading) return (
    <div style={{ padding: 60, textAlign: 'center', color: '#94a3b8', fontSize: 15 }}>
      Загрузка...
    </div>
  );
  if (!data) return (
    <div style={{ padding: 60, textAlign: 'center', color: '#ef4444', fontSize: 15 }}>
      Диалог не найден
    </div>
  );

  const score = data.evaluation?.total_score;
  const scoreColor = score ? getScoreColor(score) : '#94a3b8';
  const scoreBg = score ? getScoreBg(score) : '#f8fafc';

  return (
    <div style={styles.container}>
      {/* Back Button */}
      <button
        onClick={() => navigate(-1)}
        onMouseEnter={() => setBackHover(true)}
        onMouseLeave={() => setBackHover(false)}
        style={{
          ...styles.backBtn,
          color: backHover ? '#6366f1' : '#94a3b8',
        }}
      >
        \u2190 Назад
      </button>

      {/* Header */}
      <div style={styles.header}>
        <div>
          <h1 style={styles.title}>Диалог с {data.client_name || 'клиентом'}</h1>
          <div style={{ marginTop: 8 }}>
            <span style={{
              fontSize: 12, color: '#64748b', background: '#f1f5f9',
              padding: '4px 12px', borderRadius: 20,
            }}>
              {data.channel}
            </span>
          </div>
        </div>
      </div>

      {/* Evaluation Summary */}
      {data.evaluation && (
        <>
          {/* Score Circle + Level */}
          <div style={styles.scoreSection}>
            <div style={{
              width: 100, height: 100, borderRadius: '50%',
              background: scoreBg, border: `3px solid ${scoreColor}`,
              display: 'flex', flexDirection: 'column' as const,
              alignItems: 'center', justifyContent: 'center',
              flexShrink: 0,
            }}>
              <div style={{ fontSize: 32, fontWeight: 700, color: scoreColor, lineHeight: 1 }}>
                {score}
              </div>
              <div style={{ fontSize: 10, color: '#94a3b8', marginTop: 2 }}>из 100</div>
            </div>
            <div style={{ flex: 1 }}>
              <div style={{
                display: 'inline-block', padding: '5px 16px', borderRadius: 20,
                fontSize: 14, fontWeight: 600,
                background: scoreBg, color: scoreColor,
                border: `1px solid ${scoreColor}20`,
                marginBottom: 8,
              }}>
                {data.evaluation.level}
              </div>
              {data.evaluation.summary && (
                <p style={{ fontSize: 14, color: '#475569', lineHeight: 1.6, margin: 0 }}>
                  {data.evaluation.summary}
                </p>
              )}
            </div>
          </div>

          {/* Strengths / Weaknesses / Missed */}
          <div style={styles.pillsRow}>
            {data.evaluation.strengths?.length > 0 && (
              <div style={styles.pillCard}>
                <h4 style={{ fontSize: 14, fontWeight: 600, color: '#059669', margin: '0 0 12px 0' }}>
                  \u2713 Сильные стороны
                </h4>
                <div style={{ display: 'flex', flexWrap: 'wrap' as const, gap: 8 }}>
                  {data.evaluation.strengths.map((s: string, i: number) => (
                    <span key={i} style={{
                      background: '#ecfdf5', color: '#059669', padding: '5px 14px',
                      borderRadius: 20, fontSize: 13, fontWeight: 500,
                      border: '1px solid #a7f3d0',
                    }}>{s}</span>
                  ))}
                </div>
              </div>
            )}
            {data.evaluation.weaknesses?.length > 0 && (
              <div style={styles.pillCard}>
                <h4 style={{ fontSize: 14, fontWeight: 600, color: '#d97706', margin: '0 0 12px 0' }}>
                  \u26A0 Зоны роста
                </h4>
                <div style={{ display: 'flex', flexWrap: 'wrap' as const, gap: 8 }}>
                  {data.evaluation.weaknesses.map((w: string, i: number) => (
                    <span key={i} style={{
                      background: '#fef3c7', color: '#92400e', padding: '5px 14px',
                      borderRadius: 20, fontSize: 13, fontWeight: 500,
                      border: '1px solid #fde68a',
                    }}>{w}</span>
                  ))}
                </div>
              </div>
            )}
            {data.evaluation.missed_opportunities?.length > 0 && (
              <div style={styles.pillCard}>
                <h4 style={{ fontSize: 14, fontWeight: 600, color: '#dc2626', margin: '0 0 12px 0' }}>
                  \u2717 Упущенные возможности
                </h4>
                <div style={{ display: 'flex', flexWrap: 'wrap' as const, gap: 8 }}>
                  {data.evaluation.missed_opportunities.map((m: string, i: number) => (
                    <span key={i} style={{
                      background: '#fef2f2', color: '#dc2626', padding: '5px 14px',
                      borderRadius: 20, fontSize: 13, fontWeight: 500,
                      border: '1px solid #fecaca',
                    }}>{m}</span>
                  ))}
                </div>
              </div>
            )}
          </div>
        </>
      )}

      {/* Messages */}
      <div style={styles.messagesCard}>
        <h3 style={styles.cardTitle}>Переписка</h3>
        <div style={{ display: 'flex', flexDirection: 'column' as const, gap: 16 }}>
          {data.messages?.map((m: any, i: number) => {
            const isManager = m.sender === 'manager';
            return (
              <div key={i} style={{
                display: 'flex',
                flexDirection: 'column' as const,
                alignItems: isManager ? 'flex-end' : 'flex-start',
                maxWidth: '80%',
                alignSelf: isManager ? 'flex-end' : 'flex-start',
              }}>
                <div style={{
                  padding: '12px 18px',
                  borderRadius: isManager ? '16px 16px 4px 16px' : '16px 16px 16px 4px',
                  fontSize: 14,
                  lineHeight: 1.6,
                  background: isManager
                    ? 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)'
                    : '#f1f5f9',
                  color: isManager ? '#fff' : '#0f172a',
                  boxShadow: isManager
                    ? '0 2px 8px rgba(99, 102, 241, 0.25)'
                    : '0 1px 3px rgba(0,0,0,0.06)',
                }}>
                  <div style={{
                    fontSize: 11, fontWeight: 600, marginBottom: 4,
                    opacity: 0.75,
                  }}>
                    {isManager ? 'Менеджер' : 'Клиент'}
                  </div>
                  <div>{m.text}</div>
                  <div style={{
                    fontSize: 10, marginTop: 6,
                    color: isManager ? 'rgba(255,255,255,0.6)' : '#94a3b8',
                    textAlign: 'right' as const,
                  }}>
                    {new Date(m.time).toLocaleString('ru-RU')}
                  </div>
                </div>
                {m.ai_note && (
                  <div style={{
                    fontSize: 12, color: '#6366f1',
                    background: '#eef2ff',
                    padding: '8px 14px',
                    borderRadius: 10,
                    marginTop: 6,
                    border: '1px solid #e0e7ff',
                    maxWidth: '100%',
                  }}>
                    {'\uD83E\uDD16'} {m.ai_note}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

const styles: Record<string, React.CSSProperties> = {
  container: {
    padding: '32px 36px',
    maxWidth: 860,
    fontFamily: 'system-ui, -apple-system, sans-serif',
  },
  backBtn: {
    background: 'none',
    border: 'none',
    color: '#94a3b8',
    cursor: 'pointer',
    fontSize: 14,
    marginBottom: 20,
    padding: 0,
    transition: 'color 0.15s ease',
    fontFamily: 'system-ui, -apple-system, sans-serif',
  },
  header: {
    marginBottom: 28,
  },
  title: {
    fontSize: 26,
    fontWeight: 700,
    color: '#0f172a',
    margin: 0,
    letterSpacing: '-0.3px',
  },
  scoreSection: {
    display: 'flex',
    alignItems: 'center',
    gap: 24,
    background: '#fff',
    borderRadius: 12,
    padding: 24,
    border: '1px solid #e2e8f0',
    boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
    marginBottom: 20,
  },
  pillsRow: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))',
    gap: 16,
    marginBottom: 24,
  },
  pillCard: {
    background: '#fff',
    borderRadius: 12,
    padding: 20,
    border: '1px solid #e2e8f0',
    boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
  },
  messagesCard: {
    background: '#fff',
    borderRadius: 12,
    padding: 24,
    border: '1px solid #e2e8f0',
    boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
    display: 'flex',
    flexDirection: 'column' as const,
  },
  cardTitle: {
    fontSize: 16,
    fontWeight: 600,
    marginBottom: 20,
    color: '#0f172a',
  },
};

export default DialogueDetail;
